import { clone } from '@jack-henry/frontend-utils/functions';
import '@treasury/domain-components/layout/tm-list-layout';
import { AchRecipientRecord } from '@treasury/domain/ach';
import { TmBaseComponent } from '@treasury/presentation';
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import { TmPaginationControls } from '@treasury/presentation/components/vendor/tm-pagination-controls';
import { css, html, nothing, PropertyValues } from 'lit';
import { columnBodyRenderer } from 'lit-vaadin-helpers';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { filterTableResults } from '../../../utilities/table-result-filter';

export const tagName = 'ach-recipient-activity-table';
@customElement(tagName)
export class AchRecipientActivityTable extends TmBaseComponent {
    @property({ type: Boolean })
    public loading = true;

    @property({ type: Boolean })
    public isDesktop = false;

    @property({ type: Array })
    public achRecipientActivity: AchRecipientRecord[] = [];

    @property({ type: Array })
    public filteredAchRecipientActivity: AchRecipientRecord[] = [];

    @property({ type: Boolean })
    public initiatedFilter = false;

    @state()
    private searchTerm = '';

    @state()
    private filteredAchRecipientActivityCount = 0;

    @query('tm-pagination-controls')
    private paginationControls!: TmPaginationControls;

    private get columns() {
        if (this.isDesktop) {
            return html`<vaadin-grid-sort-column
                    header="Recipient Name"
                    path="name"
                    auto-width
                    ${columnBodyRenderer(
                        (achRecipient: AchRecipientRecord) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${achRecipient.name}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Record Type"
                    path="recordType"
                    auto-width
                    ${columnBodyRenderer(
                        (achRecipient: AchRecipientRecord) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${achRecipient.recordType}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Transaction ID / Template Name"
                    path="transactionIdTemplateName"
                    auto-width
                    ${columnBodyRenderer(
                        (achRecipient: AchRecipientRecord) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm"
                                    >${achRecipient.transactionIdTemplateName}</span
                                >
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Account"
                    path="accountNumber"
                    ${columnBodyRenderer(
                        (achRecipient: AchRecipientRecord) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${achRecipient.accountNumber}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="CR/DR"
                    path="transactionType"
                    ${columnBodyRenderer(
                        (achRecipient: AchRecipientRecord) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${achRecipient.transactionType}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Reversal"
                    path="reversal"
                    ${columnBodyRenderer(
                        (achRecipient: AchRecipientRecord) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${achRecipient.reversal ? 'Yes' : 'No'}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Amount"
                    path="amount"
                    text-align="end"
                    ${columnBodyRenderer(
                        (achRecipient: AchRecipientRecord) =>
                            html`<div class="flex flex-col justify-start">
                                <span class="text-sm font-medium"
                                    >${achRecipient.amount.toLocaleString('en-US', {
                                        style: 'currency',
                                        currency: 'USD',
                                    })}</span
                                >
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Hold"
                    path="hold"
                    ${columnBodyRenderer(
                        (achRecipient: AchRecipientRecord) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${achRecipient.hold ? 'Yes' : 'No'}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Prenote"
                    path="prenote"
                    ${columnBodyRenderer(
                        (achRecipient: AchRecipientRecord) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${achRecipient.prenote ? 'Yes' : 'No'}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>
                <vaadin-grid-sort-column
                    header="Effective Date"
                    auto-width
                    path="effectiveDateUnix"
                    ${columnBodyRenderer(
                        (achRecipient: AchRecipientRecord) =>
                            html`<div class="flex flex-col">
                                <span class="text-sm">${achRecipient.effectiveDate}</span>
                            </div>`,
                        []
                    )}
                ></vaadin-grid-sort-column>`;
        }
        return html` <vaadin-grid-column
                path="name"
                ${columnBodyRenderer(
                    (achRecipient: AchRecipientRecord) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm font-medium">${achRecipient.name}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-column>
            <vaadin-grid-column
                path="amount"
                ${columnBodyRenderer(
                    (achRecipient: AchRecipientRecord) =>
                        html`<div class="flex flex-col items-end">
                            <span class="text-sm font-medium"
                                >${achRecipient.amount.toLocaleString('en-US', {
                                    style: 'currency',
                                    currency: 'USD',
                                })}</span
                            >
                            <small class="text-xs text-tertiary"
                                >${achRecipient.effectiveDate}</small
                            >
                        </div>`,
                    []
                )}
            ></vaadin-grid-column>`;
    }

    protected render() {
        return html`<div class="flex flex-col h-full">
            <div class="flex-none">
                <tm-list-header
                    .filterButton=${true}
                    .searchTerm=${this.searchTerm}
                    @filterButtonClick=${() =>
                        this.dispatchEvent(new CustomEvent('filter-ach-recipient-activity'))}
                    @search-term-change=${(e: TmSearchTermChangeEvent) => this.filterResults(e)}
                ></tm-list-header>
            </div>
            ${this.renderTable()}
        </div>`;
    }

    protected renderPaginationControls() {
        if (!this.isDesktop) {
            return nothing;
        }
        return html`<tm-pagination-controls
            .totalItemCount=${this.filteredAchRecipientActivityCount}
            @page-changed=${() => {
                this.filterResults();
            }}
        ></tm-pagination-controls>`;
    }

    protected updated(_changedProperties: PropertyValues): void {
        if (_changedProperties.has('achRecipientActivity')) {
            this.filteredAchRecipientActivity = this.achRecipientActivity;
        }
    }

    private filterResults(e?: CustomEvent) {
        let filteredAchRecipientActivity = clone(this.achRecipientActivity);
        if (e) {
            this.searchTerm = e.detail.value;
        }
        filteredAchRecipientActivity = filterTableResults(
            {
                detail: { value: this.searchTerm },
            } as CustomEvent,
            this.achRecipientActivity,
            [
                'name',
                'transactionIdTemplateName',
                'accountNumber',
                'transactionType',
                'account',
                'amount',
                'effectiveDate',
            ]
        );
        this.filteredAchRecipientActivityCount = filteredAchRecipientActivity.length;
        if (this.isDesktop) {
            const offset = this.paginationControls?.calculateOffset();
            const limit = this.paginationControls?.pageSize;
            this.filteredAchRecipientActivity = filteredAchRecipientActivity.slice(
                offset,
                offset + limit
            );
        } else {
            this.filteredAchRecipientActivity = filteredAchRecipientActivity;
        }
    }

    private renderTable() {
        if (this.loading) {
            return html`<tm-loader card></tm-loader>`;
        }
        if (!this.achRecipientActivity.length) {
            return html`<div class="w-full px-4 py-8 text-center text-sm text-secondary">
                ${this.initiatedFilter
                    ? `No ACH recipient activity found`
                    : html`<h3 class="font-medium text-base pb-2">Enter Search Criteria</h3>
                          Find specific recipients by entering your search criteria in the filters,
                          then click search.`}
            </div>`;
        }
        return html`<div class="flex-grow">
                <vaadin-grid
                    class=${classMap({
                        'h-full': true,
                        'shadow-md': !this.isDesktop,
                    })}
                    .items=${this.filteredAchRecipientActivity}
                    @active-item-changed=${({ detail }: CustomEvent) => {
                        if (!detail.value) {
                            return;
                        }
                        this.dispatchEvent(
                            new CustomEvent('ach-recipient-transaction-select', {
                                detail: detail.value,
                            })
                        );
                    }}
                >
                    ${this.columns}
                </vaadin-grid>
            </div>
            ${this.isDesktop
                ? html`<div class="flex-none">${this.renderPaginationControls()}</div>`
                : nothing}`;
    }

    public static get styles() {
        return [
            css`
                vaadin-grid::part(body-cell) {
                    height: 100%;
                    padding: 8px 0;
                }
                vaadin-grid::part(cell) {
                    align-items: center;
                }
                vaadin-grid::part(header-cell) {
                    min-height: auto;
                    padding-top: 3px;
                    background-color: var(--border-color);
                    font-size: 0.75rem;
                    line-height: calc(1 / 0.75);
                }
                vaadin-grid-sorter::part(indicators) {
                    opacity: 0.5;
                }
                vaadin-grid-sorter[direction]::part(indicators) {
                    opacity: 100;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AchRecipientActivityTable;
    }
}
